import React, { useState, useEffect } from "react";
import { PlayerData } from "@/api/player";
import { Loader } from "../common/loader";
import { useIsMobile } from "@/hooks/use-mobile";

export interface SkillCategory {
    label: string;
    percentile: number;
    value: number;
    max: number;
    color: string;
}

interface PlayerPizzaChartProps {
    playerData: PlayerData | undefined;
    isLoading: boolean;
    chartOnly?: boolean;
    darkMode?: boolean;
    avgKpi?: number;
    skillCategories?: SkillCategory[];
}

interface ChartDataPoint {
    category: string;
    value: number;
    percentage: number;
    radius: number;
    color: string;
    startAngle: number;
    endAngle: number;
    maxValue: number;
    percentile?: number;
}

const CHART_CONFIG = {
    innerRadius: 45,
    outerRadiusBase: 90,
    center: 150,
    animationDuration: 1200,
    anglePerSlice: 360 / 5,
};

const getSvgBadgeColors = (value: number): { bg: string; text: string; border: string } => {
    if (value >= 70) return { bg: "#f0fdf4", text: "#15803d", border: "#dcfce7" };
    if (value >= 60) return { bg: "#fefce8", text: "#a16207", border: "#fef9c3" };
    if (value >= 50) return { bg: "#fff7ed", text: "#c2410c", border: "#ffedd5" };
    if (value >= 40) return { bg: "#fef2f2", text: "#f87171", border: "#fee2e2" };
    return { bg: "#fee2e2", text: "#b91c1c", border: "#fecaca" };
};

const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);
const calculatePercentage = (value: number, max: number) => (value / max) * 100;
const percentageToRadius = (percentage: number) => 0.15 + (Math.min(percentage, 100) / 100) * 0.85;

const PlayerPizzaChart: React.FC<PlayerPizzaChartProps> = ({
    playerData,
    isLoading,
    darkMode = false,
    avgKpi,
    skillCategories,
}) => {
    const [animationProgress, setAnimationProgress] = useState(0);
    const [isAnimating, setIsAnimating] = useState(false);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (!playerData || isLoading) return;

        setIsAnimating(true);
        setAnimationProgress(0);

        const startTime = Date.now();
        const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / CHART_CONFIG.animationDuration, 1);
            const easedProgress = easeOutCubic(progress);

            setAnimationProgress(easedProgress);

            if (progress < 1) {
                requestAnimationFrame(animate);
            } else {
                setIsAnimating(false);
            }
        };

        setTimeout(() => requestAnimationFrame(animate), 100);
    }, [playerData, isLoading]);

    if (isLoading && !playerData) {
        return (
            <div className="flex items-center justify-center h-full min-h-64">
                <Loader
                    message="Loading performance overview..."
                    className={darkMode ? "text-white/70 text-sm" : "text-sm"}
                />
            </div>
        );
    }

    if (!playerData) {
        return (
            <div className="flex items-center justify-center h-full min-h-64">
                <p className={darkMode ? "text-white/50" : "text-on-surface-variant"}>No performance data available</p>
            </div>
        );
    }

    const isGoalkeeper = playerData.role.includes("GK");

    let chartData: ChartDataPoint[];

    if (isGoalkeeper) {
        const goalkeeperKpis = playerData.goalkeeperKpis || [];
        const kpiAvgs = playerData.kpiLeagueAvgsGoalkeeper;

        const avgGSAx =
            goalkeeperKpis.length > 0
                ? goalkeeperKpis.reduce((sum, kpi) => sum + (kpi.pointsGSAx.pointsGSAx || 0), 0) / goalkeeperKpis.length
                : 0;
        const avgHighDanger =
            goalkeeperKpis.length > 0
                ? goalkeeperKpis.reduce((sum, kpi) => sum + (kpi.zone1.pointsHighDangerShots || 0), 0) /
                  goalkeeperKpis.length
                : 0;
        const avgLowDanger =
            goalkeeperKpis.length > 0
                ? goalkeeperKpis.reduce((sum, kpi) => sum + (kpi.zone4.pointsLowDangerShots || 0), 0) /
                  goalkeeperKpis.length
                : 0;
        const avgLateralPass =
            goalkeeperKpis.length > 0
                ? goalkeeperKpis.reduce((sum, kpi) => sum + (kpi.lateralPass.pointsLateralPassShots || 0), 0) /
                  goalkeeperKpis.length
                : 0;
        const avgScreened =
            goalkeeperKpis.length > 0
                ? goalkeeperKpis.reduce((sum, kpi) => sum + (kpi.screened.pointsScreenedShots || 0), 0) /
                  goalkeeperKpis.length
                : 0;

        chartData = [
            {
                category: "GSAx Points",
                value: avgGSAx,
                maxValue: kpiAvgs?.pointsGSAxAvg ?? 30,
                color: "var(--color-general-orange)",
                percentage: 0,
                radius: 0,
                startAngle: 0,
                endAngle: 0,
            },
            {
                category: "High Danger Shots",
                value: avgHighDanger,
                maxValue: kpiAvgs?.pointsHighDangerShotsAvg ?? 20,
                color: "var(--color-general-red)",
                percentage: 0,
                radius: 0,
                startAngle: 0,
                endAngle: 0,
            },
            {
                category: "Low Danger Shots",
                value: avgLowDanger,
                maxValue: kpiAvgs?.pointsLowDangerShotsAvg ?? 30,
                color: "var(--color-general-green)",
                percentage: 0,
                radius: 0,
                startAngle: 0,
                endAngle: 0,
            },
            {
                category: "Lateral Pass Shots",
                value: avgLateralPass,
                maxValue: kpiAvgs?.pointsLateralPassShotsAvg ?? 10,
                color: "var(--color-general-yellow)",
                percentage: 0,
                radius: 0,
                startAngle: 0,
                endAngle: 0,
            },
            {
                category: "Screened Shots",
                value: avgScreened,
                maxValue: kpiAvgs?.pointsScreenedShotsAvg ?? 10,
                color: "var(--color-general-purple)",
                percentage: 0,
                radius: 0,
                startAngle: 0,
                endAngle: 0,
            },
        ];
    } else {
        const kpis = playerData.kpis || [];
        const kpiAvgs = playerData.kpiLeagueAvgs;

        const avgOffensive =
            kpis.length > 0 ? kpis.reduce((sum, kpi) => sum + (kpi.offensiveData.points || 0), 0) / kpis.length : 0;
        const avgDefensive =
            kpis.length > 0 ? kpis.reduce((sum, kpi) => sum + (kpi.defensiveData.points || 0), 0) / kpis.length : 0;
        const avgTeamPlay =
            kpis.length > 0 ? kpis.reduce((sum, kpi) => sum + (kpi.teamPlayData.points || 0), 0) / kpis.length : 0;
        const avgEffort =
            kpis.length > 0 ? kpis.reduce((sum, kpi) => sum + (kpi.effortData.points || 0), 0) / kpis.length : 0;
        const avgBonus =
            kpis.length > 0 ? kpis.reduce((sum, kpi) => sum + (kpi.bonusData.points || 0), 0) / kpis.length : 0;

        chartData = [
            {
                category: "Offensive",
                value: avgOffensive,
                maxValue: kpiAvgs?.offensivePointsAvg ?? 24,
                color: "var(--color-general-red)",
                percentage: 0,
                radius: 0,
                startAngle: 0,
                endAngle: 0,
            },
            {
                category: "Defensive",
                value: avgDefensive,
                maxValue: kpiAvgs?.defensivePointsAvg ?? 36,
                color: "var(--color-general-green)",
                percentage: 0,
                radius: 0,
                startAngle: 0,
                endAngle: 0,
            },
            {
                category: "Team Play",
                value: avgTeamPlay,
                maxValue: kpiAvgs?.teamPlayPointsAvg ?? 30,
                color: "var(--color-general-yellow)",
                percentage: 0,
                radius: 0,
                startAngle: 0,
                endAngle: 0,
            },
            {
                category: "Effort",
                value: avgEffort,
                maxValue: kpiAvgs?.effortPointsAvg ?? 10,
                color: "var(--color-general-purple)",
                percentage: 0,
                radius: 0,
                startAngle: 0,
                endAngle: 0,
            },
            {
                category: "Bonus",
                value: avgBonus,
                maxValue: kpiAvgs?.bonusPointsAvg ?? 10,
                color: "var(--color-general-orange)",
                percentage: 0,
                radius: 0,
                startAngle: 0,
                endAngle: 0,
            },
        ];
    }

    chartData = chartData.map((item, index) => {
        const valuePercentage = calculatePercentage(item.value, item.maxValue);
        const skillMatch = skillCategories?.find((s) => s.color === item.color);
        const percentile = skillMatch?.percentile;
        const radiusPercentage = percentile ?? valuePercentage;
        return {
            ...item,
            percentage: valuePercentage,
            radius: percentageToRadius(radiusPercentage),
            startAngle: index * CHART_CONFIG.anglePerSlice,
            endAngle: (index + 1) * CHART_CONFIG.anglePerSlice,
            percentile,
        };
    });

    const createSlicePath = (slice: ChartDataPoint) => {
        const animatedOuterRadius =
            CHART_CONFIG.innerRadius + CHART_CONFIG.outerRadiusBase * slice.radius * animationProgress;
        const startAngleRad = ((slice.startAngle - 90) * Math.PI) / 180;
        const endAngleRad = ((slice.endAngle - 90) * Math.PI) / 180;

        const points = [
            [CHART_CONFIG.innerRadius, startAngleRad],
            [animatedOuterRadius, startAngleRad],
            [animatedOuterRadius, endAngleRad],
            [CHART_CONFIG.innerRadius, endAngleRad],
        ].map(([r, angle]) => [CHART_CONFIG.center + r * Math.cos(angle), CHART_CONFIG.center + r * Math.sin(angle)]);

        const largeArcFlag = slice.endAngle - slice.startAngle <= 180 ? "0" : "1";

        return [
            `M ${points[0][0]} ${points[0][1]}`,
            `L ${points[1][0]} ${points[1][1]}`,
            `A ${animatedOuterRadius} ${animatedOuterRadius} 0 ${largeArcFlag} 1 ${points[2][0]} ${points[2][1]}`,
            `L ${points[3][0]} ${points[3][1]}`,
            `A ${CHART_CONFIG.innerRadius} ${CHART_CONFIG.innerRadius} 0 ${largeArcFlag} 0 ${points[0][0]} ${points[0][1]}`,
            "Z",
        ].join(" ");
    };

    const renderSliceText = (slice: ChartDataPoint, index: number) => {
        const sliceDelay = index * 0.15;
        const sliceProgress = Math.max(0, Math.min(1, (animationProgress - sliceDelay) / (1 - sliceDelay)));

        if (sliceProgress <= 0.5) return null;

        const midAngle = (slice.startAngle + slice.endAngle) / 2;
        const animatedOuterRadius =
            CHART_CONFIG.innerRadius + CHART_CONFIG.outerRadiusBase * slice.radius * animationProgress;
        const actualRadius = animatedOuterRadius - CHART_CONFIG.innerRadius;
        const isInside = actualRadius >= 25;
        const textRadius = isInside ? (CHART_CONFIG.innerRadius + animatedOuterRadius) / 2 : animatedOuterRadius + 15;

        const midAngleRad = ((midAngle - 90) * Math.PI) / 180;
        const textX = CHART_CONFIG.center + textRadius * Math.cos(midAngleRad);
        const textY = CHART_CONFIG.center + textRadius * Math.sin(midAngleRad);

        return (
            <text
                key={`slice-text-${slice.category}`}
                x={textX}
                y={textY}
                textAnchor="middle"
                dominantBaseline="central"
                className={`text-base font-bold ${textFill}`}
                opacity={Math.min(1, (sliceProgress - 0.5) * 2)}
            >
                {(slice.value * animationProgress).toFixed(1)}
            </text>
        );
    };

    const renderSeparatorLine = (slice: ChartDataPoint) => {
        if (animationProgress <= 0.3) return null;

        const startAngleRad = ((slice.startAngle - 90) * Math.PI) / 180;
        const innerPoint = [
            CHART_CONFIG.center + CHART_CONFIG.innerRadius * Math.cos(startAngleRad),
            CHART_CONFIG.center + CHART_CONFIG.innerRadius * Math.sin(startAngleRad),
        ];
        const outerPoint = [
            CHART_CONFIG.center + 153 * Math.cos(startAngleRad),
            CHART_CONFIG.center + 153 * Math.sin(startAngleRad),
        ];

        const lineProgress = Math.max(0, (animationProgress - 0.3) / 0.7);

        return (
            <line
                key={`separator-${slice.category}`}
                x1={innerPoint[0]}
                y1={innerPoint[1]}
                x2={innerPoint[0] + (outerPoint[0] - innerPoint[0]) * lineProgress}
                y2={innerPoint[1] + (outerPoint[1] - innerPoint[1]) * lineProgress}
                stroke="rgba(255, 255, 255, 0.2)"
                strokeWidth="1"
                opacity={lineProgress}
            />
        );
    };

    const renderPercentileRing = (slice: ChartDataPoint) => {
        if (slice.percentile === undefined || animationProgress <= 0.5) return null;

        const ringProgress = Math.max(0, (animationProgress - 0.5) / 0.5);
        const r = 142;
        const cx = CHART_CONFIG.center;
        const cy = CHART_CONFIG.center;

        const startRad = ((slice.startAngle - 90) * Math.PI) / 180;
        const fullEndRad = ((slice.endAngle - 90) * Math.PI) / 180;

        const filledSpanDeg =
            (slice.endAngle - slice.startAngle) * (Math.min(slice.percentile, 100) / 100) * ringProgress;
        const filledEndRad = ((slice.startAngle - 90 + filledSpanDeg) * Math.PI) / 180;

        const makeArc = (fromRad: number, toRad: number) => {
            const x1 = cx + r * Math.cos(fromRad);
            const y1 = cy + r * Math.sin(fromRad);
            const x2 = cx + r * Math.cos(toRad);
            const y2 = cy + r * Math.sin(toRad);
            const largeArc = Math.abs(toRad - fromRad) > Math.PI ? 1 : 0;
            return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
        };

        return (
            <g key={`pct-ring-${slice.category}`} opacity={ringProgress}>
                <path
                    d={makeArc(startRad, fullEndRad)}
                    fill="none"
                    stroke="rgba(255,255,255,0.15)"
                    strokeWidth="5"
                    strokeLinecap="butt"
                />
                {slice.percentile > 1 && filledSpanDeg > 0.3 && (
                    <path
                        d={makeArc(startRad, filledEndRad)}
                        fill="none"
                        stroke={slice.color}
                        strokeWidth="5"
                        strokeLinecap="butt"
                        opacity={0.9}
                    />
                )}
            </g>
        );
    };

    const renderCategoryLabel = (slice: ChartDataPoint) => {
        if (animationProgress <= 0.6) return null;

        const labelProgress = Math.max(0, (animationProgress - 0.6) / 0.4);
        const midAngle = (slice.startAngle + slice.endAngle) / 2;
        const midAngleRad = ((midAngle - 90) * Math.PI) / 180;

        const labelR = 165;
        const lx = CHART_CONFIG.center + labelR * Math.cos(midAngleRad);
        const ly = CHART_CONFIG.center + labelR * Math.sin(midAngleRad);

        const needsFlip = midAngle > 90 && midAngle < 315;
        const rotation = needsFlip ? midAngle - 180 : midAngle;

        const hasPercentile = slice.percentile !== undefined;
        const lineSpacing = isMobile ? 15 : 12;
        const halfSpacing = hasPercentile ? lineSpacing / 2 : 0;

        return (
            <g key={`label-${slice.category}`} opacity={labelProgress} transform={`rotate(${rotation}, ${lx}, ${ly})`}>
                <text
                    x={lx}
                    y={ly - halfSpacing}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontWeight="700"
                    fontSize={isMobile ? "11" : "9"}
                    fill={darkMode ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.7)"}
                    letterSpacing="0.04em"
                >
                    {slice.category}
                </text>
                {hasPercentile && (
                    <text
                        x={lx}
                        y={ly + halfSpacing}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontWeight="900"
                        fontSize={isMobile ? "12" : "10"}
                        fill={slice.color}
                        stroke={darkMode ? "rgba(5,0,102,0.7)" : "rgba(255,255,255,0.9)"}
                        strokeWidth="3"
                        paintOrder="stroke"
                    >
                        {slice.percentile}%
                    </text>
                )}
            </g>
        );
    };

    const textFill = darkMode ? "fill-white" : "fill-on-surface";
    const textFillMuted = darkMode ? "fill-white/60" : "fill-on-surface-variant";

    const renderKpiGauge = () => {
        if (avgKpi === undefined || animationProgress <= 0.4) return null;
        const gaugeProgress = Math.min(1, (animationProgress - 0.4) / 0.4);
        const kpiPct = Math.min(Math.max(avgKpi / 100, 0), 1);
        const gaugeR = 36;
        const cx = CHART_CONFIG.center;
        const cy = CHART_CONFIG.center;
        const arcSpan = 270;
        const arcStartDeg = -225;
        const arcStartRad = (arcStartDeg * Math.PI) / 180;
        const arcEndDeg = arcStartDeg + arcSpan;
        const arcEndRad = (arcEndDeg * Math.PI) / 180;

        const describeArc = (startRad: number, endRad: number, r: number) => {
            const x1 = cx + r * Math.cos(startRad);
            const y1 = cy + r * Math.sin(startRad);
            const x2 = cx + r * Math.cos(endRad);
            const y2 = cy + r * Math.sin(endRad);
            return `M ${x1} ${y1} A ${r} ${r} 0 1 1 ${x2} ${y2}`;
        };

        const trackPath = describeArc(arcStartRad, arcEndRad, gaugeR);

        const filledAngleDeg = arcSpan * kpiPct * gaugeProgress;
        const filledEndRad = (arcStartDeg + filledAngleDeg) * (Math.PI / 180);
        const filledLarge = filledAngleDeg > 180 ? 1 : 0;
        const fx1 = cx + gaugeR * Math.cos(arcStartRad);
        const fy1 = cy + gaugeR * Math.sin(arcStartRad);
        const fx2 = cx + gaugeR * Math.cos(filledEndRad);
        const fy2 = cy + gaugeR * Math.sin(filledEndRad);
        const filledPath =
            filledAngleDeg > 0 ? `M ${fx1} ${fy1} A ${gaugeR} ${gaugeR} 0 ${filledLarge} 1 ${fx2} ${fy2}` : null;

        return (
            <g opacity={gaugeProgress}>
                <path
                    d={trackPath}
                    fill="none"
                    stroke={darkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}
                    strokeWidth="4"
                    strokeLinecap="round"
                />
                {filledPath && (
                    <path
                        d={filledPath}
                        fill="none"
                        stroke={darkMode ? "#ffffff" : "#050066"}
                        strokeWidth="4"
                        strokeLinecap="round"
                    />
                )}
                {(() => {
                    const colors = getSvgBadgeColors(avgKpi);
                    const displayVal = (avgKpi * gaugeProgress).toFixed(1);
                    const bw = 40;
                    const bh = 20;
                    const bx = cx - bw / 2;
                    const by = cy - bh / 2 - 4;
                    return (
                        <>
                            <rect
                                x={bx}
                                y={by}
                                width={bw}
                                height={bh}
                                rx="3"
                                ry="3"
                                fill={colors.bg}
                                stroke={colors.border}
                                strokeWidth="1"
                            />
                            <text
                                x={cx}
                                y={by + bh / 2}
                                textAnchor="middle"
                                dominantBaseline="central"
                                fontSize="12"
                                fontWeight="900"
                                fill={colors.text}
                                fontFamily="inherit"
                            >
                                {displayVal}
                            </text>
                            <text
                                x={cx}
                                y={by + bh + 7}
                                textAnchor="middle"
                                dominantBaseline="central"
                                fontSize="5.5"
                                fontWeight="700"
                                fill={darkMode ? "rgba(255,255,255,0.5)" : "rgba(0,0,66,0.5)"}
                                letterSpacing="0.08em"
                            >
                                KPI
                            </text>
                        </>
                    );
                })()}
            </g>
        );
    };

    return (
        <div className="flex items-center justify-center w-full h-full">
            <svg width="100%" height="100%" viewBox="-70 -70 440 440">
                {chartData.map((slice, index) => {
                    const sliceDelay = index * 0.15;
                    const sliceProgress = Math.max(0, Math.min(1, (animationProgress - sliceDelay) / (1 - sliceDelay)));

                    return (
                        <path
                            key={slice.category}
                            d={createSlicePath(slice)}
                            fill={slice.color}
                            stroke="rgba(255, 255, 255, 0.1)"
                            strokeWidth="1"
                            opacity={0.9 * sliceProgress}
                            style={{
                                transform: `scale(${sliceProgress})`,
                                transformOrigin: "150px 150px",
                                transition: isAnimating ? "none" : "all 0.3s ease",
                            }}
                        />
                    );
                })}

                {chartData.map((slice, index) => renderSliceText(slice, index))}
                {chartData.map(renderSeparatorLine)}
                {chartData.map(renderPercentileRing)}
                {chartData.map(renderCategoryLabel)}

                <circle
                    cx={CHART_CONFIG.center}
                    cy={CHART_CONFIG.center}
                    r={CHART_CONFIG.innerRadius}
                    fill={darkMode ? "rgba(5, 0, 102, 0.95)" : "rgba(245, 242, 241, 0.97)"}
                    stroke="rgba(148, 163, 184, 0.2)"
                    strokeWidth="1"
                    opacity={animationProgress}
                />

                {renderKpiGauge()}

                {avgKpi === undefined && animationProgress > 0.4 && (
                    <g opacity={Math.min(1, (animationProgress - 0.4) / 0.3)}>
                        <text
                            x={CHART_CONFIG.center}
                            y="146"
                            textAnchor="middle"
                            dominantBaseline="central"
                            fontSize="11"
                            fontWeight="900"
                            className={textFill}
                        >
                            {(chartData.reduce((s, d) => s + d.value, 0) * animationProgress).toFixed(1)}
                        </text>
                        <text
                            x={CHART_CONFIG.center}
                            y="160"
                            textAnchor="middle"
                            dominantBaseline="central"
                            fontSize="6"
                            fontWeight="600"
                            className={textFillMuted}
                        >
                            TOTAL PTS
                        </text>
                    </g>
                )}
            </svg>
        </div>
    );
};

export default PlayerPizzaChart;
